'use client'

import { useEffect, useRef } from 'react'

import { Button, Form, Input, InputRef } from 'antd'
import { selectChats } from 'features/ai-assistant/model/selectors/aiAssistantSelectors'
import { renameChatThunk } from 'features/ai-assistant/model/slice/aiAssistantSlice'
import { assetsSelectors, renameAssetsThunk } from 'features/assets/model/slice/assetsSlice'
import { boardsSelectors, renameBoardThunk } from 'features/boards/model/slice/boardsSlice'
import { BoardToolbarAction } from 'shared/constants/toolbar'
import { selectModalContext, selectModalData, selectModalType } from 'features/common/model/selectors/modalSelectors'
import { closeModal } from 'features/common/model/slice/moadlSlice'
import { getFilesPreviewIsPublicLinkSelector } from 'features/files-preview/model/selectors/filesPreviewSelectors'
import { renamePublicLinkFileThunk } from 'features/public-link-files/model/slice/publicLinkFilesSlice'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { BaseModal } from 'shared/ui/BaseModal'
import styled from 'styled-components'

export const RenameItemModal = () => {
  const dispatch = useAppDispatch()
  const [form] = Form.useForm()
  const inputRef = useRef<InputRef>(null)

  const modalType = useAppSelector(selectModalType)
  const modalContext = useAppSelector(selectModalContext)
  const modalData = useAppSelector(selectModalData)
  const isPublicLink = useAppSelector(getFilesPreviewIsPublicLinkSelector)

  const itemId = modalData?.ids?.[0]

  const chats = useAppSelector(selectChats)

  const storeItem: any = useAppSelector(state => {
    if (modalContext === 'board') {
      return boardsSelectors.selectById(state.boards, itemId)
    } else if (modalContext === 'asset') {
      return assetsSelectors.selectById(state.assets, itemId)
    } else if (modalContext === 'chat') {
      return chats.find(chat => chat.id === itemId)
    }
    return null
  })

  const passedItem = modalData?.items?.[0] as any

  const item = storeItem ?? passedItem

  const getItemName = (ctx: typeof modalContext, item: any) => {
    if (!item) return ''
    if (ctx === 'chat') return item.title ?? ''
    return item.name ?? ''
  }

  useEffect(() => {
    const itemName = getItemName(modalContext, item)

    if (itemName) {
      form.setFieldsValue({ name: itemName })
    }

    return () => {
      form.resetFields()
    }
  }, [getItemName(modalContext, item), form, modalContext])

  const handleFocus = () => {
    setTimeout(() => {
      const inputEl = inputRef.current?.input
      const name = form.getFieldValue('name')
  
      if (inputEl && typeof name === 'string') {
        if (modalContext === 'board' || modalContext === 'chat') {
          inputEl.select()
        } else {
          const lastDotIndex = name.lastIndexOf('.')
          const isDotValid = lastDotIndex > 0 && lastDotIndex < name.length - 1
  
          if (isDotValid) {
            inputEl.setSelectionRange(0, lastDotIndex)
          } else {
            inputEl.select()
          }
        }
      }
    }, 0)
  }
  

  const handleSubmit = (values: { name: string }) => {
    if (!itemId) return

    if (isPublicLink) {
      dispatch(renamePublicLinkFileThunk({ id: itemId, name: values.name, type: modalContext }))
    } else if (modalContext === 'board') {
      dispatch(renameBoardThunk({ name: values.name, boardId: itemId }))
    } else if (modalContext === 'asset') {
      dispatch(renameAssetsThunk({ name: values.name, assetsId: itemId }))
    } else if (modalContext === 'chat') {
      dispatch(renameChatThunk({ name: values.name, chatId: itemId }))
    }

    dispatch(closeModal())
  }

  const handleCancel = () => {
    dispatch(closeModal())
  }

  return (
    <BaseModal
      title={`Rename ${modalContext}`}
      isOpen={modalType === BoardToolbarAction.Rename}
      onClose={handleCancel}
    >
      <SContentContainer>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark="optional"
        >
          <Form.Item
            name="name"
            label={`${modalContext} name`}
            rules={[{ required: true, message: `Please enter a name for the ${modalContext}` }]}
          >
            <Input
              ref={inputRef}
              autoFocus
              onFocus={handleFocus}
            />
          </Form.Item>

          <SButtonsContainer>
            <Button 
              size="large"
              onClick={handleCancel}
            >
              Cancel
            </Button>

            <Button 
              type="primary"
              size="large"
              htmlType="submit"
            >
              Rename
            </Button>
          </SButtonsContainer>
        </Form>
      </SContentContainer>
    </BaseModal>
  )
}

const SButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
`

const SContentContainer = styled.div`
  margin-top: 24px;
`